import React, { useEffect, useState } from 'react';
import { Container, Table, Button, Modal, Form, Row, Col } from 'react-bootstrap';
import ReactQuill from 'react-quill';

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [formData, setFormData] = useState({ title: '', desc: '', images: [], feature_blog: 'No' });
  const [imagePath, setImagePath] = useState('');
  const [imagePreviews, setImagePreviews] = useState([]);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch('https://api.biznetusa.com/api/all-blogs');
      const data = await response.json();
      setBlogs(data.blogs);
      setImagePath(data.imagePath);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`https://api.biznetusa.com/api/deleteblogs/${id}`, {
        method: 'DELETE',
      });
      setBlogs(blogs.filter((blog) => blog.id !== id));
    } catch (error) {
      console.error('Error deleting blog:', error);
    }
  };

  const handleEdit = (blog) => {
    setSelectedBlog(blog);
    setFormData({ 
      title: blog.title, 
      desc: blog.desc, 
      images: [], 
      feature_blog: blog.feature_blog || 'No' 
    });
    setImagePreviews(blog.images.map((img) => `${imagePath}/${img.image}`));
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedBlog) return;

    try {
      const updatedBlog = new FormData();
      updatedBlog.append('id', selectedBlog.id);
      updatedBlog.append('title', formData.title);
      updatedBlog.append('desc', formData.desc);
      updatedBlog.append('feature_blog', formData.feature_blog);

      if (formData.images.length > 0) {
        formData.images.forEach((image) => updatedBlog.append('image[]', image));
      }

      const response = await fetch(`https://api.biznetusa.com/api/update-blogs/${selectedBlog.id}`, {
        method: 'PUT',
        body: updatedBlog,
      });

      if (response.ok) {
        await fetchBlogs();
        setShowEditModal(false);
        setSelectedBlog(null);
        setFormData({ title: '', desc: '', images: [], feature_blog: 'No' });
        setImagePreviews([]);
      } else {
        const errorData = await response.json();
        console.error('Failed to update blog:', errorData);
      }
    } catch (error) {
      console.error('Error updating blog:', error);
    }
  };

  const handlePostSubmit = async () => {
    try {
      const newBlog = new FormData();
      newBlog.append('title', formData.title);
      newBlog.append('desc', formData.desc);
      newBlog.append('feature_blog', formData.feature_blog);
      formData.images.forEach((image) => newBlog.append('image[]', image));

      const response = await fetch('https://api.biznetusa.com/api/store-blogs', {
        method: 'POST',
        body: newBlog,
      });

      if (response.ok) {
        fetchBlogs();
        setShowPostModal(false);
        setFormData({ title: '', desc: '', images: [], feature_blog: 'No' });
        setImagePreviews([]);
      }
    } catch (error) {
      console.error('Error posting blog:', error);
    }
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setFormData({ ...formData, images: files });
    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  return (
    <Container>
      <h1 className="my-4">Blog Management</h1>
      <Button className="mb-3" onClick={() => setShowPostModal(true)}>
        Add New Blog
      </Button>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Title</th>
            <th>Description</th>
            <th>Feature Blog</th>
            <th>Images</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {blogs.map((blog) => (
            <tr key={blog.id}>
              <td>{blog.id}</td>
              <td>{blog.title}</td>
              <td dangerouslySetInnerHTML={{ __html: blog.desc }}></td>
              <td>{blog.feature_blog}</td>
              <td>
                {blog.images.map((image) => (
                  <img
                    key={image.id}
                    src={`${imagePath}/${image.image}`}
                    alt="Blog"
                    style={{ width: '50px', marginRight: '10px' }}
                  />
                ))}
              </td>
              <td>
                <Button variant="warning" size="sm" onClick={() => handleEdit(blog)}>
                  Edit
                </Button>{' '}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(blog.id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Form.Group>
            <Form.Group controlId="formDesc" className="mt-3">
              <Form.Label>Description</Form.Label>
              <ReactQuill
                theme="snow"
                value={formData.desc}
                onChange={(value) => setFormData({ ...formData, desc: value })}
                style={{ height: '300px', maxHeight: '400px', overflowY: 'auto' }}
              />
            </Form.Group>
            <Form.Group controlId="formFeatureBlog" className="mt-3">
              <Form.Label>Feature Blog</Form.Label>
              <Form.Select
                value={formData.feature_blog}
                onChange={(e) => setFormData({ ...formData, feature_blog: e.target.value })}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="formImages" className="mt-3">
              <Form.Label>Images</Form.Label>
              <Form.Control type="file" multiple onChange={handleImageChange} />
              <Row className="mt-3">
                {imagePreviews.map((preview, index) => (
                  <Col xs={6} md={3} key={index} className="mb-3">
                    <img src={preview} alt="Preview" className="img-thumbnail" />
                  </Col>
                ))}
              </Row>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Post Modal */}
      <Modal show={showPostModal} onHide={() => setShowPostModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </Form.Group>
            <Form.Group controlId="formDesc" className="mt-3">
              <Form.Label>Description</Form.Label>
              <ReactQuill
                theme="snow"
                value={formData.desc}
                onChange={(value) => setFormData({ ...formData, desc: value })}
                style={{ height: '300px', maxHeight: '400px', overflowY: 'auto' }}
              />
            </Form.Group>
            <Form.Group controlId="formFeatureBlog" className="mt-3">
              <Form.Label>Feature Blog</Form.Label>
              <Form.Select
                value={formData.feature_blog}
                onChange={(e) => setFormData({ ...formData, feature_blog: e.target.value })}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="formImages" className="mt-3">
              <Form.Label>Images</Form.Label>
              <Form.Control type="file" multiple onChange={handleImageChange} />
              <Row className="mt-3">
                {imagePreviews.map((preview, index) => (
                  <Col xs={6} md={3} key={index} className="mb-3">
                    <img src={preview} alt="Preview" className="img-thumbnail" />
                  </Col>
                ))}
              </Row>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPostModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handlePostSubmit}>
            Add Blog
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BlogManager;